import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { isAwaitingProcessing } from '../../utils/articleReadiness';
import { persistFeedFilters } from './feedFilterPersistence';
import './CreateBriefComponent.css';
import {api} from "../../app/http-common";
import { useConfig } from '../../contexts/FrontendConfigContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useCanEdit, useCanCreateBrief, useIsAdmin, useUserInfo } from '../../hooks/useFrontegg';
import { useDrag } from 'react-dnd';
import ErrorModal from '../../components/ErrorModal';
import AppModal from '../../components/AppModal/AppModal';
import LottieLoader from '../../components/LottieLoader'; // Lottie animation loader
import ArticlesList from './ArticlesList';
import {
    convertTimeRangeToDateRange,
    extractKeywords,
    languages,
    getSourceTitle2
} from './transformers';
import { OUTPUT_LANGUAGE_NAMES, toOutputLanguageName } from '../../utils/languages';
import {useNavigate, useMatch, useLocation} from "react-router-dom";
import {
    selectArticles,
    selectClaims,
    selectDontShowAgainSetBriefLanguage,
    selectLastSelectedArticles,
    selectLastSelectedClaims,
    selectPristine,
    selectSearchParams,
    selectSelectedArticlesArray,
    selectSelectedClaimsArray,
    selectStory, selectUserSelectedBriefLanguage,
    selectActiveTab,
    selectCurrentStep,
    selectStories,
    selectStoryLocal,
    selectErrorStories,
    selectLoadingArticles,
    selectHasMoreStories,
    selectIsLoadingMoreStories,
    selectTotalStories,
    selectNavigateBack,
} from '../../app/store';
import {batch, useDispatch, useSelector} from 'react-redux';
import {
    clearSelectedArticlesArray,
    clearSelectedClaims,
    clearSelectedClaimsArray,
    setArticles,
    setClaims,
    setPublisherMap,
    setSelectedArticlesArray,
    setSelectedClaimsArray,
    setSelectedPublishers,
    setStory,
    updateSelectedClaimsOrder,
    setLastSelectedClaims,
    setLastSelectedArticles,
    setSelectedBriefLanguage,
    setDontShowAgainSetBriefLanguage,
    setUserSelectedBriefLanguage, clearSelectedStoryIndex, setPrs,
    setSelectedCategories,
    setActiveTab as setActiveTabAction,
    setCurrentStep as setCurrentStepAction,
    setStories as setStoriesAction,
    appendStories as appendStoriesAction,
    setStoryLocal as setStoryLocalAction,
    setErrorStories as setErrorStoriesAction,
    setLoadingArticles as setLoadingArticlesAction,
    setHasMoreStories as setHasMoreStoriesAction,
    setIsLoadingMoreStories as setIsLoadingMoreStoriesAction,
    setTotalStories as setTotalStoriesAction,
    setNavigateBack as setNavigateBackAction,
    setSignalsOnly,
} from './slice';
import SearchFields from "./SearchFields";
import StepIndicator from "./StepIndicator";

import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import BriefComponent from "./BriefComponent";
import { STORY_BUCKET_TYPE } from "./BucketSidebar";
import StoriesBucketPanel from "./StoriesBucketPanel";
import AddToBucketModal from "./AddToBucketModal";
import BriefGenerationSettingsModal from './BriefGenerationSettingsModal';
import { SAVED_BRIEF_CREATED_EVENT } from '../MyBriefs/events';
import {
    clearPersistedBriefState,
    persistBriefState,
} from './briefStatePersistence';
import { SLOTS, peekReturn } from '../../app/navContext';

import StoriesFeed from "./StoriesFeed";
import './ScreenLayouts.css';
import FineTuning from "../FineTuning/FineTuning";
import { isDesignFixtureMode, getDesignFixtureStep, FIXTURE_STORIES, FIXTURE_STORY, FIXTURE_ARTICLES, FIXTURE_CLAIMS, FIXTURE_BRIEF } from './designFixtures';

// ReactModal.setAppElement('#root'); // Assuming your app's root element has the id 'root'

export const parseBackfillCount = (raw, fallback = 20) => {
    if (raw === null || raw === undefined || raw === '') return fallback;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : fallback;
};

/**
 * What the stories feed should say about a failed search.
 *
 * A keyword the story-title text index cannot answer falls back to an
 * unindexed regex over article title+body; past a couple of days that exceeds
 * the backend's Mongo time budget. The backend reports it as an actionable
 * 422 with a SEARCH_TIMEOUT code, which is a search the user can fix — not the
 * raw axios "Request failed with status code 400" the feed used to print.
 *
 * Returns an object for an explained failure, a plain string otherwise; the
 * feed renders the string under a generic "Error loading stories" title.
 */
export const resolveStoriesError = (err) => {
    const data = err?.response?.data;
    if (data?.code === 'SEARCH_TIMEOUT') {
        return {
            title: 'Search too broad',
            message: data.message || 'This search took too long to run. Try a shorter time range or fewer keywords.',
        };
    }
    return err?.message || 'Something went wrong loading stories';
};

/**
 * The output language a freshly picked bucket implies, or null to leave the
 * Language select alone.
 *
 * A bucket's targetLanguage is the language that bucket wants its drafts
 * produced in, but nothing used to carry it into generation — a German bucket
 * produced English drafts because the Language select stayed on its default.
 * Selecting a bucket now copies it in, visibly, so it stays overridable.
 * Returns null for "None", an unknown bucket, or a language we cannot deliver
 * in (setting the select to a value outside its options renders it blank).
 */
export const resolveBucketLanguage = (bucketLists, bucketId) => {
    if (!bucketId) return null;
    const all = (bucketLists || []).flatMap((list) => (Array.isArray(list) ? list : []));
    const picked = all.find((b) => b && String(b._id) === String(bucketId));
    return toOutputLanguageName(picked?.targetLanguage);
};

/**
 * What the feed query that produced a story row actually narrowed by.
 *
 * The article page cannot tell "your filter excluded this" from "the feed row
 * simply did not contain it" by set difference alone, because `/stories_search`
 * narrows a story's `articles` by things the article page never asked about —
 * above all the TIME WINDOW, which is always applied and defaults to the last
 * 24 hours. `/articles/:storyId` applies no date filter at all and returns the
 * whole all-time cluster, so every older article in a long-running story came
 * back as a non-match and was labelled "Outside this filter" even with no
 * filter selected. That was the reported bug.
 *
 * So the page classifies against the query that ran instead of against the
 * subset it returned. Only the filters the backend applies PER ARTICLE count as
 * filters here: keywords, categories, languages, countries and feed source
 * (`getSortedStories` in backend/news_graph_utils.js). `excludeLanguages` and
 * `excludeCountries` are deliberately absent — both are enforced at story level
 * by the source-share classifier, never as an article `$nin`, so neither can
 * explain a missing article.
 */
export const resolveFeedArticleScope = (query, story) => {
    if (!query || typeof query !== 'object') return null;
    const selected = (value) => (Array.isArray(value) ? value.filter(v => v && v !== 'none') : []);
    const keywords = selected(query.keywords);
    const articleFilters = [
        ...keywords,
        ...selected(query.categories),
        ...selected(query.languages),
        ...selected(query.countries),
        ...selected(query.feedSource),
    ];
    const toDate = (value) => {
        if (!value) return null;
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    };
    return {
        // Start only. `dateEnd` is always "now at query time" for every preset
        // the feed offers, so it describes nothing the user chose — see
        // resolveOpenedStoryArticles below.
        windowStart: toDate(query.dateStart),
        timeRangeLabel: query.timeRange || null,
        hasArticleFilter: articleFilters.length > 0,
        // The feed told us its subset is a truncated prefix, so its absences
        // carry no information at all.
        subsetTruncated: story?.articlesTruncated === true,
    };
};

/**
 * Mark which of a story's articles the feed row accounted for, and why the rest
 * are not in it. Matching first, the rest below, muted.
 *
 * Three distinct facts, three flags, because they have three different causes
 * and only one of them is about a filter:
 *  - `outsideTimeRange` — published outside the window the feed was browsing.
 *    Decided from the article's own `dateTimePub` against that window, not by
 *    subtraction, so it is true whether or not the row happened to be complete.
 *  - `extra` — inside the window, but absent from a row whose narrowing we can
 *    attribute to a filter the user actually set. NOT set when no per-article
 *    filter was active, and not set when the row was truncated: an absence we
 *    cannot explain must not be reported as an exclusion.
 *  - unready articles are neither; `isAwaitingProcessing` owns that, because the
 *    article page returns `subjectClustered` articles the feed excludes by phase.
 *
 * Both groups stay in the list and both stay readable. There is no Show-all
 * toggle, because nothing is hidden.
 *
 * THE BACKFILL IS GONE, deliberately. It used to promote non-matching articles
 * to `extra: false` until twenty were "matched", which is what produced the
 * reported card-says-8 / page-shows-14. Worse than the count mismatch: it did it
 * by CLEARING the flag, destroying the very distinction the page now needs, so a
 * reader could not tell which articles answered their filter. A story with three
 * matching articles should look like a story with three matching articles.
 *
 * `scope` omitted means "we do not know what ran" — the deep-link path has no
 * feed query behind it — and falls back to the plain set difference.
 */
export const resolveOpenedStoryArticles = (canonicalArticles = [], matchedArticleIds = [], scope = null) => {
    if (!Array.isArray(canonicalArticles)) return [];

    const matchedSet = new Set(matchedArticleIds);
    const windowStart = scope?.windowStart ? new Date(scope.windowStart).getTime() : null;
    // Absent scope keeps the legacy behaviour; a known scope only attributes an
    // absence to a filter when one was active and the row was whole.
    const attributesToFilter = scope ? (scope.hasArticleFilter && !scope.subsetTruncated) : true;

    // Only the window's START excludes. Every time range the feed offers is a
    // "Last N hours" preset whose `dateEnd` is just the moment the query ran
    // (`convertTimeRangeToDateRange`), so an article NEWER than it did not fall
    // outside the user's range — it simply arrived after the feed looked, and
    // calling the freshest article in a story "outside the selected time range"
    // would be its own small lie.
    const outsideWindow = (article) => {
        if (windowStart === null) return false;
        const published = article?.dateTimePub ? new Date(article.dateTimePub).getTime() : NaN;
        if (Number.isNaN(published)) return false;
        return published < windowStart;
    };

    const result = canonicalArticles.map((item) => {
        if (!item || typeof item !== 'object') return item;
        const {
            extra: _stripped,
            backfilled: _alsoStripped,
            outsideTimeRange: _andThis,
            ...canonicalArticle
        } = item;
        if (outsideWindow(item)) {
            canonicalArticle.outsideTimeRange = true;
        } else if (attributesToFilter && matchedSet.size > 0) {
            canonicalArticle.extra = !matchedSet.has(item._id);
        }
        return canonicalArticle;
    });

    // Matching first, otherwise original order. A stable partition rather than a
    // sort, so the relative order the backend chose survives inside each group.
    const demoted = (a) => Boolean(a && typeof a === 'object' && (a.extra || a.outsideTimeRange));
    if (!result.some(demoted)) return result;
    return [
        ...result.filter(a => !demoted(a)),
        ...result.filter(demoted),
    ];
};

const CreateBriefComponent = ({ onStepChange, onStepHandlersReady, initialStep }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { STORIES_INITIAL_PAGE_SIZE, STORIES_LOAD_MORE_PAGE_SIZE } = useConfig();
    // Extract slug from URL using useMatch (works with layout routes where useParams
    // only sees the parent route's params, not child route params)
    const storyMatch = useMatch('/story/:slug/*');
    const urlSlug = storyMatch?.params?.slug;
    const location = useLocation();
    const {
        keywords,
        selectedPublishers,
        publicationsData ,
        dateRange,
        queryUrl,
        selectedCategories,
        selectedLanguages,
        excludeLanguages,
        excludeCountries,
        selectedCountries,
        countryCoverageMode,
        languageCoverageMode,
        selectedBriefLanguage,
        selectedTimeRange,
        storiesFromDate,
        excludeAlreadyMadeStories,
        signalsOnly,
        prs,
        verifiedBySourcesToggle,
        minSourcesVerified,
        sentimentFilter,
        advancedSort,
        feedSourceFilter,
    } = useSelector(selectSearchParams);
    // `?trending=1` turns the Trending filter on once, on arrival.
    //
    // This is where the retired /signals page redirects to and what the header
    // link points at, so a bookmark of the old page still lands somewhere
    // sensible. Applied once rather than on every render: after that it is an
    // ordinary control, and re-applying it would fight an operator who turned
    // it off without leaving the page.
    const trendingParamAppliedRef = useRef(false);
    useEffect(() => {
        if (trendingParamAppliedRef.current) return;
        trendingParamAppliedRef.current = true;
        if (new URLSearchParams(window.location.search).get('trending') === '1') {
            dispatch(setSignalsOnly(true));
        }
    }, [dispatch]);

    const articles = useSelector(selectArticles);
    const dontShowAgainSetBriefLanguage = useSelector(selectDontShowAgainSetBriefLanguage);
    const userSelectedBriefLanguage = useSelector(selectUserSelectedBriefLanguage);
    const claims = useSelector(selectClaims);
    const story = useSelector(selectStory);
    const selectedClaims = useSelector(selectSelectedClaimsArray);
    const selectedArticles = useSelector(selectSelectedArticlesArray);

    const lastSelectedClaims = useSelector(selectLastSelectedClaims);
    const lastSelectedArticles = useSelector(selectLastSelectedArticles);

    // Funnel state from Redux
    const activeTab = useSelector(selectActiveTab);
    const currentStep = useSelector(selectCurrentStep);
    const stories = useSelector(selectStories);
    const storyLocal = useSelector(selectStoryLocal);
    const errorStrories = useSelector(selectErrorStories);
    const loadingArticles = useSelector(selectLoadingArticles);
    const hasMoreStories = useSelector(selectHasMoreStories);
    const isLoadingMoreStories = useSelector(selectIsLoadingMoreStories);
    const totalStories = useSelector(selectTotalStories);
    const navigateBack = useSelector(selectNavigateBack);

    const { markStep: markOnboardingStep, enabled: onboardingActive, state: onboardingState } = useOnboarding();
    // First-time flow: until the user has visited the drafts page once, the
    // post-generation modal offers only "Go to my draft" (no back-to-stories).
    const onboardingFirstDraftPending = onboardingActive && !onboardingState?.steps?.sawDraft;

    // Dispatch wrappers for funnel state (keeps setter-style API for minimal code changes)
    const setActiveTab = useCallback((v) => dispatch(setActiveTabAction(v)), [dispatch]);
    const setCurrentStep = useCallback((v) => dispatch(setCurrentStepAction(v)), [dispatch]);
    const setStories = useCallback((v) => dispatch(setStoriesAction(v)), [dispatch]);
    const setStoryLocal = useCallback((v) => dispatch(setStoryLocalAction(v)), [dispatch]);
    const setErrorStrories = useCallback((v) => dispatch(setErrorStoriesAction(v)), [dispatch]);
    const setLoadingArticles = useCallback((v) => dispatch(setLoadingArticlesAction(v)), [dispatch]);
    const setHasMoreStories = useCallback((v) => dispatch(setHasMoreStoriesAction(v)), [dispatch]);
    const setIsLoadingMoreStories = useCallback((v) => dispatch(setIsLoadingMoreStoriesAction(v)), [dispatch]);
    const setTotalStories = useCallback((v) => dispatch(setTotalStoriesAction(v)), [dispatch]);
    const setNavigateBack = useCallback((v) => dispatch(setNavigateBackAction(v)), [dispatch]);

    // Deep-link: load story from URL when arriving at /story/:slug
    // Track which slug was loaded to avoid re-loading the same story
    const deepLinkLoadedSlugRef = useRef(null);
    const deepLinkBucketRef = useRef({ slug: null, bucketId: null });
    useEffect(() => {
        if (fixtureMode) return;
        if (!urlSlug || urlSlug === 'undefined') return;

        // §2 — a story opened FROM a bucket carries that bucket, so the article
        // screen can resolve the restricted-production context for this exact
        // (bucket, story) pair. Read from the URL rather than remembered state,
        // so a refresh or a shared link behaves the same as the first open.
        //
        // Deliberately ABOVE the already-loaded guard below: re-opening a story
        // that is already loaded — from the feed first, then from its bucket —
        // changes the restriction even though it does not change the articles,
        // and skipping this left that second open unrestricted.
        const bucketFromUrl = new URLSearchParams(window.location.search).get('bucket');
        // Remembered against the slug it arrived with, because the step-sync
        // effect below rewrites the URL and would otherwise drop the parameter
        // on the first render — making a plain refresh silently un-restrict the
        // story. Keyed by slug so it cannot leak into the next story opened
        // from the feed, which has no bucket.
        const previousDeepLinkBucketId = deepLinkBucketRef.current.bucketId;
        deepLinkBucketRef.current = { slug: urlSlug, bucketId: bucketFromUrl || null };
        if (bucketFromUrl) setTargetBucketId(bucketFromUrl);
        // Only ever clear a bucket this effect itself set. The same state also
        // holds the author's remembered Draft Formatting choice, which a story
        // opened without a bucket must not wipe.
        else if (previousDeepLinkBucketId) setTargetBucketId('');

        // Skip if we already loaded this exact slug (e.g., from in-app navigation)
        if (deepLinkLoadedSlugRef.current === urlSlug) return;
        deepLinkLoadedSlugRef.current = urlSlug;

const loadStoryFromSlug = async () => {
            setLoadingArticles(true);
            try {
                const response = await api.storyBySlug(urlSlug);
                if (response && response.story) {
                    const storyData = response.story;
                    // No feed query behind a deep link, so there is no scope to
                    // judge these articles against and nothing is labelled.
                    const storyArticles = resolveOpenedStoryArticles(response.articles || []);

                    batch(() => {
                        setOpenedStoryScope(null);
                        dispatch(setStory(storyData));
                        setStoryLocal(storyData);
                        dispatch(setArticles(storyArticles));
                        setActiveTab('searchArticles');
                        setCurrentStep(1);
                        setAutoSelect(true);
                        setNavigateBack(true);
                    });
                }
            } catch (err) {
                if (err?.response?.status === 404) {
                    // Stories get merged into one another and age out, so an old link
                    // or bookmark stops resolving. That is ordinary, not a fault —
                    // say so and return to the feed instead of reporting an error.
                    console.warn('Story link no longer resolves, returning to the feed:', urlSlug);
                    showToastNotification(
                        'That story is no longer available — it may have been merged into another story.',
                        'info',
                    );
                } else {
                    console.error('Error loading story from URL:', err);
                }
                navigate('/', { replace: true });
            } finally {
                setLoadingArticles(false);
            }
        };

        loadStoryFromSlug();
    }, [urlSlug]);

    const [briefResponse, setBriefResponse] = useState(null);
    const [briefSourceArticles, setBriefSourceArticles] = useState([]);
    const [mergeStories, setMergeStories] = useState(false);
    const [loadingBrief, setLoadingBrief] = useState(false);
    const [selectedTitleIndex, setSelectedTitleIndex] = useState(0);
    const canEdit = useCanEdit();
    const isAdmin = useIsAdmin();
    const userInfo = useUserInfo();
    const [bucketSidebarOpen, setBucketSidebarOpen] = useState(() => {
        try { return localStorage.getItem('bucketSidebarOpen') === 'true'; } catch { return false; }
    });
    const [bucketSidebarWidth, setBucketSidebarWidth] = useState(280);
    const toggleBucketSidebar = useCallback(() => {
        setBucketSidebarOpen(prev => {
            const next = !prev;
            try { localStorage.setItem('bucketSidebarOpen', String(next)); } catch {}
            return next;
        });
    }, []);
    const _canCreateBrief = useCanCreateBrief();
    const [publishers, setPublishers] = useState([]);
    // State for managing which tab is active
    const [reviewSelectedArticles, setReviewSelectedArticles] = useState(false);
    const [reviewSelectedClaims, setReviewSelectedClaims] = useState(false);
    const [autoSelect, setAutoSelect] = useState(false);
    const lastStoriesQueryRef = useRef(null);

    // The country/language filter that produced the stories on screen, in the
    // shape the suggestion stores. Derived from the SUBMITTED query rather than
    // the current control values, so a half-edited filter the user has not run
    // yet is never recorded as the scope a suggestion was collected under.
    const [activeCollectionScope, setActiveCollectionScope] = useState(null);
    // The full feed scope the currently open story was opened under — wider than
    // activeCollectionScope, which only covers country/language. Kept so the
    // article-page summary can name the time range it is talking about instead
    // of calling every absence "this filter".
    const [openedStoryScope, setOpenedStoryScope] = useState(null);
    const loadMoreAbortControllerRef = useRef(null);
    const skipNextFetchRef = useRef(false); // Skip stories re-fetch when doing soft back-navigation
    // Every onSearchClear() turns the feed spinner on and relies on the fetch
    // effect below re-running to turn it off. That effect is keyed on the
    // filter values, so a clear that changes nothing — Reset All pressed twice,
    // or pressed when no filter is set — never re-ran it, and the feed sat on a
    // spinner with no request in flight until a full page reload. This nonce
    // makes "I asked for a refetch" a fact of its own rather than something
    // inferred from a value changing.
    const [feedResetNonce, setFeedResetNonce] = useState(0);
    const [clear, setClear] = useState(false);
    const [claimsUpdated,setClaimsUpdated] = useState(false);
    const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
    const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
    const modalRef = useRef(null);
    const promptModalRef = useRef(null);
    const pendingClaimsRef = useRef(null);
    const isFirstRun = useRef(true);
    const currentStepRef = useRef(currentStep); // Ref to hold the latest value of currentStep
    const storiesAbortControllerRef = useRef(null); // Ref for AbortController to cancel stories_search
    const handleSubmitStoriesRef = useRef(null); // Ref to always call the latest handleSubmitStories (avoids stale closure in intervals)
    const [expandedTimeRange, setExpandedTimeRange] = useState(null); // Set when backend auto-expands due to empty results
    // Add state for toast notification
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('success'); // 'success', 'error', or 'info'
    const [toastAction, setToastAction] = useState(null); // { label, onClick } for actionable toasts
    const [hasCopiedDraft, setHasCopiedDraft] = useState(false);
    const [selectedBriefPromptType, setSelectedBriefPromptType] = useState('DEFAULT_BRIEF_2_CITATIONS'); // Default prompt: "Two"-citation style
    const [selectedModel, setSelectedModel] = useState('gpt-5'); // Add state for model selection
    const [selectedTranslationModel, setSelectedTranslationModel] = useState('gpt'); // Add state for model Translation selection
    const [selectedReformatModel, setSelectedReformatModel] = useState(''); // Empty = fall back to generation model (or DB default BRIEF_REFORMAT_MODEL)
    const [generationMethod, setGenerationMethod] = useState('claims'); // 'claims', 'claimFree', or 'both' (admin only)
    const [targetBucketId, setTargetBucketId] = useState(''); // admin-only: per-bucket AI reformatting
    // Publisher Bucket Intelligence (spec §7). Populated when a target bucket
    // is selected AND the current story has been resolved. `null` means the
    // check hasn't run yet — the modal treats null as "no constraint".
    const [bucketCompetitorContext, setBucketCompetitorContext] = useState(null);
    // True while the per-(bucket, story) settings are being fetched. The method
    // lock and the restricted selection both derive from them, so the screen
    // must not accept a generation request until they have settled.
    const [bucketContextLoading, setBucketContextLoading] = useState(false);
    const [adminBuckets, setAdminBuckets] = useState([]);
    useEffect(() => {
        if (!isAdmin) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await api.getPublisherBuckets();
                const list = Array.isArray(data?.buckets) ? data.buckets : (Array.isArray(data) ? data : []);
                if (!cancelled) setAdminBuckets(list.filter((b) => b && b.isActive !== false));
            } catch (_err) {
                if (!cancelled) setAdminBuckets([]);
            }
        })();
        return () => { cancelled = true; };
    }, [isAdmin]);
    // The buckets this user owns whose reformatting they may apply to their own
    // drafts. A user can own several, so the AUTHOR picks per draft; the
    // default is None (no reformatting at all).
    const [ownedBuckets, setOwnedBuckets] = useState([]);
    // Per-user key so a shared browser never leaks one author's choice to another.
    const reformatChoiceKey = userInfo?.email ? `alchemiq.draftReformatBucket.${userInfo.email}` : null;
    useEffect(() => {
        if (isAdmin) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const data = await api.getMyPublisherBuckets({ reformattingOnly: true });
                const list = Array.isArray(data?.buckets) ? data.buckets : (Array.isArray(data) ? data : []);
                if (cancelled) return;
                setOwnedBuckets(list);
                // Restore the last choice, but only while it is still on offer:
                // ownership can be revoked or reformatting switched off between
                // drafts, and a stale id would silently resolve to no
                // reformatting server-side with no visible reason.
                let stored = null;
                try {
                    stored = reformatChoiceKey ? window.localStorage.getItem(reformatChoiceKey) : null;
                } catch (_e) { stored = null; }
                if (stored && list.some((b) => String(b._id) === stored)) setTargetBucketId(stored);
            } catch (_err) {
                if (!cancelled) setOwnedBuckets([]);
            }
        })();
        return () => { cancelled = true; };
    }, [isAdmin, reformatChoiceKey]);
    const [comparisonBriefs, setComparisonBriefs] = useState(null); // For 'both' mode: { claimsBased: {...}, claimFree: {...} }
    const [generationErrors, setGenerationErrors] = useState(null); // For admin: errors from generation
    const [isSavedBriefStartedModalOpen, setIsSavedBriefStartedModalOpen] = useState(false);
    
    // Error modal state
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorModalData, setErrorModalData] = useState({ title: '', message: '', statusCode: null, model: '' });

    // Merge confirmation state
    const [confirmMergeOpen, setConfirmMergeOpen] = useState(false);
    const [pendingMergeStories, setPendingMergeStories] = useState(null);
    // Filter sidebar collapse
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const persistedBriefStorySlug = story?.slug || story?.groupSubject?.slug || storyLocal?.slug || urlSlug;

    const storyDragRef = useRef(null);
    const storyId = story?._id || story?.groupSubject?._id || storyLocal?._id;
    const storyTitle = story?.title || story?.groupSubject?.title || storyLocal?.title;
    const [, storyTitleDrag] = useDrag({
        type: STORY_BUCKET_TYPE,
        item: { storyId, title: storyTitle },
        canDrag: () => isAdmin && currentStep === 1 && Boolean(storyId),
    });
    if (isAdmin && currentStep === 1 && storyId) {
        storyTitleDrag(storyDragRef);
    }

    // The explicit alternative to dragging that title onto the panel, the same
    // control the Stories list carries. Orr, 2 Nov 2026: the Articles screen
    // still had only the drag. Same modal, same gates, same two-step override.
    const [addToBucketOpen, setAddToBucketOpen] = useState(false);
    // AddToBucketModal reads only `_id` and `title`, and on this screen the
    // story arrives under three different shapes depending on how it was
    // opened, which is what `storyId` and `storyTitle` above already resolve.
    const openedStoryForBucket = useMemo(
        () => (storyId ? { _id: storyId, title: storyTitle } : null),
        [storyId, storyTitle],
    );

    const fixtureMode = useMemo(() => isDesignFixtureMode(), []);

    // Auto-complete onboarding checklist items from real progress:
    // entering a story = found it, reaching Claims = sources selected,
    // opening the generation settings = claims reviewed, starting the
    // generation (settings confirmed / draft visible) = draft generated.
    // Visiting My Drafts marks the final step (handled in App.js).
    useEffect(() => {
        if (fixtureMode) return;
        if (currentStep >= 1) markOnboardingStep('foundStory');
        if (currentStep >= 2) markOnboardingStep('selectedSources');
        if (currentStep >= 3) markOnboardingStep('reviewedClaims');
    }, [currentStep, fixtureMode, markOnboardingStep]);

    useEffect(() => {
        if (fixtureMode) return;
        if (isPromptModalOpen) markOnboardingStep('reviewedClaims');
    }, [isPromptModalOpen, fixtureMode, markOnboardingStep]);

    useEffect(() => {
        if (fixtureMode) return;
        if (isSavedBriefStartedModalOpen || (currentStep === 3 && briefResponse)) {
            markOnboardingStep('generatedDraft');
        }
    }, [isSavedBriefStartedModalOpen, currentStep, briefResponse, fixtureMode, markOnboardingStep]);
    const canCreateBrief = fixtureMode ? true : _canCreateBrief;
    useEffect(() => {
        if (!fixtureMode) return;
        const step = getDesignFixtureStep();
        if (step !== null && step >= 0 && step <= 3) {
            const tabMap = { 0: 'searchStories', 1: 'searchArticles', 2: 'searchArticles', 3: 'brief' };
            setCurrentStep(step);
            setActiveTab(tabMap[step]);
            if (step === 1 || step === 2) {
                setAutoSelect(true);
            }
        }
    }, [fixtureMode]);

    // Categories fetched from API (includes virtual categories)
    const [categories, setCategories] = useState(['All categories']);

    // Loading messages for puzzle animation
    // Handlers to switch tabs
    const switchToSelectArticles = () => setActiveTab('selectArticles');


    const switchToFineTuning = () => setActiveTab('fineTuning');
    const switchToBrief = () => setActiveTab('brief');

    useEffect(() => {
        if (fixtureMode) return;
        const fetchPublisherMap = async () => {
            try {
                const response = await api.getPublishers(); // Adjust the API endpoint as needed
                if (Array.isArray(response)) {
                    const publisherMap = {
                        'none': [],
                        'israel-palestine': response.filter((pub)=>pub.bias_reference ==='israel-palestine'),
                        'ukraine-russia': response.filter((pub)=>pub.bias_reference ==='ukraine-russia'),
                        'republican-democratic': response.filter((pub)=>pub.bias_reference ==='republican-democratic'),
                        'technology': response.filter((pub)=>pub.bias_reference ==='technology'),
                        'science': response.filter((pub)=>pub.bias_reference ==='science'),
                        'streaming': response.filter((pub)=>pub.bias_reference ==='streaming'),
                    }
                    dispatch(setPublisherMap(publisherMap));
                    setPublishers(response);
                }
            } catch (error) {
                console.error('Failed to fetch publisher map:', error);
            }
        };

        fetchPublisherMap();
    }, [dispatch]); // Dependency array includes dispatch to ensure stability


    // Fetch categories from API on component mount
    useEffect(() => {
        if (fixtureMode) return;
        const fetchCategories = async () => {
            try {
                const response = await api.getCategories();
                if (response && Array.isArray(response)) {
                    // Core categories first (case-insensitive), then remaining alphabetically
                    const coreOrder = ['business', 'entertainment', 'tech', 'science', 'tourism', 'travel', 'lifestyle'];
                    const allCategoryNames = response.map(c => c.name);
                    // Match core categories case-insensitively but keep original casing
                    const coreCategories = coreOrder
                        .map(coreName => allCategoryNames.find(name => name.toLowerCase() === coreName.toLowerCase()))
                        .filter(Boolean);
                    const coreNamesLower = coreOrder.map(n => n.toLowerCase());
                    const otherCategories = allCategoryNames
                        .filter(name => !coreNamesLower.includes(name.toLowerCase()))
                        .sort((a, b) => a.localeCompare(b));
                    const categoryNames = ['All categories', ...coreCategories, '---', ...otherCategories];
                    setCategories(categoryNames);
                }
            } catch (error) {
                console.error('Error fetching categories:', error);
                // Keep default 'All categories' on error
            }
        };
        fetchCategories();
    }, []);

// Handler to be passed to ClaimsViewer
    const isAuthenticated = () => {
        const token = localStorage.getItem('token');
        // Additional validation of token can be done here
        return !!token;
    };

    // The URL is the source of truth whenever the user lands on the stories
    // feed — initial mount, browser back, or nav links. The layout route keeps
    // this component mounted across '/' <-> '/story/' navigation, so stale
    // funnel state (currentStep > 0 + old story) would otherwise keep showing
    // or re-opening the last story. Runs only on pathname changes; the funnel
    // itself never sets the path to '/' while a step is active, so any arrival
    // here with an active step is user-initiated and must reset the funnel.
    useEffect(() => {
        if (fixtureMode) return;
        const onFeed = location.pathname === '/' || location.pathname === '/news-search-with-claims';
        if (onFeed && currentStepRef.current !== 0) {
            onSearchClear();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Update ref whenever currentStep changes
    useEffect(() => {
        currentStepRef.current = currentStep;
        if (currentStep === 0) {
            dispatch(setPrs(false))
        }
        // Notify parent component about step changes
        if (onStepChange) {
            onStepChange(currentStep);
        }

        // Sync URL with current step (only when a story has a valid slug)
        const storySlug = story?.slug || story?.groupSubject?.slug || storyLocal?.slug;
        if (storySlug && storySlug !== 'undefined') {
            // Mark this slug as already loaded so deep-link effect doesn't re-fetch
            deepLinkLoadedSlugRef.current = storySlug;
            let targetPath;
            switch (currentStep) {
                case 1: targetPath = `/story/${storySlug}`; break;
                case 2: targetPath = `/story/${storySlug}/claims`; break;
                case 3: targetPath = `/story/${storySlug}/draft`; break;
                default: targetPath = '/'; break;
            }
            // Carry the originating bucket through every step of the funnel.
            // Without it the restricted-production context survived only until
            // the first URL rewrite, so a refresh re-opened the story with
            // every article selectable and no scope banner.
            const carriedBucketId = deepLinkBucketRef.current.slug === storySlug
                ? deepLinkBucketRef.current.bucketId
                : null;
            const targetUrl = carriedBucketId && targetPath.startsWith('/story/')
                ? `${targetPath}?bucket=${encodeURIComponent(carriedBucketId)}`
                : targetPath;
            const currentUrl = targetPath.startsWith('/story/')
                ? `${location.pathname}${location.search}`
                : location.pathname;
            if (currentUrl !== targetUrl) {
                navigate(targetUrl, { replace: true });
            }
        } else if (currentStep === 0 && location.pathname !== '/' && location.pathname !== '/news-search-with-claims') {
            navigate('/', { replace: true });
        }
    }, [currentStep, onStepChange, story, storyLocal]);

    // Handle navigation when articles and story are already loaded (e.g., from external source)
    useEffect(() => {
        if (fixtureMode) return;
        // Check if we have articles and a story pre-loaded. Only auto-open the
        // story on an actual story deep link — never while the user is on the
        // stories feed, where leftover state would hijack searches and the
        // Stories/logo navigation back into the old story.
        if (articles.length > 0 && story && currentStep === 0 && location.pathname.startsWith('/story/')) {
            setActiveTab('searchArticles');
            setCurrentStep(1);
            setAutoSelect(true);
            setStoryLocal(story);
        }
    }, [articles, story]);

    useEffect(() => {
        if (!persistedBriefStorySlug || !briefResponse) return;

        persistBriefState(persistedBriefStorySlug, {
            briefResponse,
            comparisonBriefs,
            briefSourceArticles,
            selectedTitleIndex,
        });
    }, [persistedBriefStorySlug, briefResponse, comparisonBriefs, briefSourceArticles, selectedTitleIndex]);

    useEffect(() => {
        let intervalId;
        let debounceTimeout;
        const debouncedSubmit = () => {
            if (fixtureMode) return;
            // When navigating back from a story, skip the re-fetch to preserve
            // the full stories list (including load-more batches) and scroll position
            if (skipNextFetchRef.current) {
                skipNextFetchRef.current = false;
                // The spinner is switched on by whoever asked for the refetch.
                // Skipping it here without switching it off again leaves the
                // feed loading forever over a list that is already correct.
                setLoadingArticles(false);
                return;
            }
            if (currentStepRef.current === 0) {
                // Load normal stories
                // selectedCategories filter is only relevant here
                if (selectedTimeRange === 'Live') {
                    handleSubmitStoriesRef.current(); // Immediate call once stable
                    intervalId = setInterval(() => {
                        if (currentStepRef.current === 0) {
                            // Use ref to always call the latest handleSubmitStories
                            // (with current keywords, filters, etc.)
                            // Pass isLiveRefresh to avoid clearing stories and resetting scroll
                            handleSubmitStoriesRef.current({ isLiveRefresh: true });
                        }
                    }, 60000); // 60000 milliseconds = 1 minute
                } else {
                    handleSubmitStoriesRef.current();
                }
            }
        };

        // Set up debounce
        debounceTimeout = setTimeout(debouncedSubmit, 500); // Wait 500ms after the last change

        return () => {
            // Clear any previous timeouts or intervals on cleanup
            clearTimeout(debounceTimeout);
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [
        activeTab,           // Trigger refetch when switching back to stories tab (e.g. after deep-link back nav)
        feedResetNonce,      // Trigger refetch on every clear, including one that changes no filter
        selectedTimeRange,
        selectedLanguages, 
        excludeLanguages,
        excludeCountries,
        selectedCountries,
        countryCoverageMode,
        languageCoverageMode,
        selectedCategories,  // Added back - needed for left sidebar category filter
        storiesFromDate, 
        excludeAlreadyMadeStories,
        signalsOnly,        // Refetch when the Trending filter is toggled
        sentimentFilter,     // Trigger refetch when sentiment filter changes
        advancedSort,        // Trigger refetch when sort option changes
        feedSourceFilter,    // Trigger refetch when feed source filter changes
    ]);

    // Cleanup: Cancel any pending stories_search requests on component unmount
    useEffect(() => {
        return () => {
            if (storiesAbortControllerRef.current) {
                storiesAbortControllerRef.current.abort();
            }
        };
    }, []);

    useEffect(() =>  {
        if (prs) {
            handleGetPRs();
        }

    }, [prs]); // Dependencies for re-triggering

    useEffect(() => {
         if (isFirstRun.current) {
            isFirstRun.current = false; // Skip the first run
            return;
        }
        let debounceTimeout;
        const handleKeywordChange = () => {
            handleSubmitStoriesRef.current(); // Use ref to always call the latest version
            window.removeEventListener('keydown', resetDebounceOnKeydown); // Remove event listener after server call
        };

        const resetDebounceOnKeydown = () => {
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(handleKeywordChange, 2500); // Wait 3 seconds after the last keystroke
        };

        // Attach keydown listener to reset debounce on each keystroke
        window.addEventListener('keydown', resetDebounceOnKeydown);

        // Initial debounce setup for keywords input
        debounceTimeout = setTimeout(handleKeywordChange, 2500);

        return () => {
            clearTimeout(debounceTimeout); // Clear timeout on cleanup
            window.removeEventListener('keydown', resetDebounceOnKeydown); // Remove event listener on cleanup
        };
    }, [keywords]); // Only watch for changes in keywords

    // Add useEffect to reapply verification filter when settings change
    useEffect(() => {
        // Only run if we're on the claims step and have claims loaded
        if (currentStep === 2 && claims.length > 0) {
            
            // Get the original unfiltered claims from lastSelectedClaims or claims
            let claimsToFilter = [...claims];
            
            // Apply verification filter if enabled
            if (verifiedBySourcesToggle && minSourcesVerified) {
                const filteredClaims = claimsToFilter.map(claim => ({
                    ...claim,
                    // Hide claims that don't meet the threshold
                    isHidden: claim.articleCount < minSourcesVerified
                }));
                
                // Update the claims with the filter applied
                dispatch(setClaims(filteredClaims));
            } else {
                // If filter is disabled, show all claims
                const allVisibleClaims = claimsToFilter.map(claim => ({
                    ...claim,
                    isHidden: false
                }));
                dispatch(setClaims(allVisibleClaims));
            }
        }
    }, [verifiedBySourcesToggle, minSourcesVerified]); // Re-run when filter settings change



    const handleGetPRs = async () => {
        setLoadingArticles(true)
        try {
            const response = await api.getPrs();
            if (response.length) {
                setActiveTab('searchArticles');
                setCurrentStep(1);
                setAutoSelect(false);
                dispatch(setArticles(response));
            } else {
                showToastNotification('No press releases found at this time.', 'info');
                dispatch(setPrs(false));
            }

        }catch (e) {
            showToastNotification('Failed to load press releases.', 'error');
            dispatch(setPrs(false));
        }
        setLoadingArticles(false)

    }
    const handleSubmitStories = async (options = {}) => {
        const { isLiveRefresh = false, userInitiated = false } = options;
        // Cancel any in-progress stories_search request
        if (storiesAbortControllerRef.current) {
            storiesAbortControllerRef.current.abort();
        }
        // Note: an empty selectedLanguages is treated as "no language filter"
        // (same as all-languages-selected). Previously this branch short-circuited
        // to an empty result set, which produced the "No Stories Found" empty
        // state after a user picked a language then removed it.

        // Create a new AbortController for this request
        storiesAbortControllerRef.current = new AbortController();

        // Set loading BEFORE clearing stories so there's never a render frame
        // where stories is empty but loading is false (which shows "No Stories Found")
        if (!isLiveRefresh) {
            setLoadingArticles(true);
            dispatch(clearSelectedStoryIndex());
        }
        if (stories.length > 0 && !isLiveRefresh) {
            setStories([]);
        }
        const _dateRange = convertTimeRangeToDateRange(selectedTimeRange);

        const extractedKeywords = extractKeywords(keywords);
        const query = {
            keywords: extractedKeywords,
            dateStart: _dateRange.dateStart,
            dateEnd: _dateRange.dateEnd,
            categories: selectedCategories.length > 0 ? selectedCategories : ['none'],
            languages: (selectedLanguages.length === 0 || selectedLanguages.length === languages.length) ? ['none'] : selectedLanguages,
            excludeLanguages,
            excludeCountries,
            countries: selectedCountries.length > 0 ? selectedCountries : ['none'],
            // §2 — how much of a story has to match. The backend falls back to
            // the stricter mode for anything it does not recognise.
            countryCoverageMode,
            languageCoverageMode,
            storiesFromDate,
            timeRange: selectedTimeRange,
            excludeAlreadyMadeStories,
            // Narrows the same feed to stories selected as Signals. Applied
            // server-side before pagination, so `total` and `hasMore` describe
            // the filtered list rather than the unfiltered one.
            signalsOnly,
            sentimentFilter,
            advancedSort,
            feedSource: feedSourceFilter, // Feed source filter: empty array = all, or array of specific sources
        };
        // Store query for loadMore
        lastStoriesQueryRef.current = query;
        // §7 — the applied filter, so a reload restores what was actually run.
        persistFeedFilters({
            selectedLanguages, selectedCountries, excludeLanguages, excludeCountries,
            selectedCategories, selectedTimeRange, countryCoverageMode, languageCoverageMode,
        });
        const scopedCountries = (query.countries || []).filter(c => c && c !== 'none');
        const scopedLanguages = (query.languages || []).filter(l => l && l !== 'none');
        setActiveCollectionScope(
            scopedCountries.length === 0 && scopedLanguages.length === 0
                ? null
                : {
                    countries: scopedCountries,
                    languages: scopedLanguages,
                    countryCoverageMode: query.countryCoverageMode || 'mostly',
                    languageCoverageMode: query.languageCoverageMode || 'mostly',
                });
        setHasMoreStories(false);
        setTotalStories(0);

        try {
            const response = await api.stories_search(query, storiesAbortControllerRef.current.signal, { skip: 0, limit: STORIES_INITIAL_PAGE_SIZE, userInitiated });
            if (response && response.stories) {
                setErrorStrories(null);
                setStories(response.stories);
                setHasMoreStories(response.hasMore);
                setTotalStories(response.total);
                setExpandedTimeRange(response.expandedTimeRange || null);
            } else if (response && Array.isArray(response)) {
                setErrorStrories(null);
                setStories(response);
                setHasMoreStories(false);
                setTotalStories(response.length);
            } else {
                setErrorStrories('Invalid response format');
            }
            setLoadingArticles(false);
        } catch (err) {
            if (err.name === 'CanceledError' || err.message?.includes('cancel')) {
                return;
            }
            const resolved = resolveStoriesError(err);
            if (typeof resolved === 'string') {
                console.error('Error fetching news:', err);
            } else {
                // Expected, actionable outcome — not a backend fault to report.
                console.warn('Stories search timed out:', resolved.message);
            }
            setErrorStrories(resolved);
            setStories([]);
            setLoadingArticles(false);
        } finally {
            storiesAbortControllerRef.current = null;
        }
    };

    // Keep ref in sync so the Live mode interval always calls the latest version
    // (with current keywords, filters, etc.) — avoids stale closure bug
    handleSubmitStoriesRef.current = handleSubmitStories;

    const handleIdleRefresh = useCallback(() => {
        if (storiesAbortControllerRef.current || loadMoreAbortControllerRef.current) return false;
        handleSubmitStoriesRef.current({ isLiveRefresh: true });
        return true;
    }, []);

    const handleLoadMoreStories = useCallback(async () => {
        if (!hasMoreStories || isLoadingMoreStories || !lastStoriesQueryRef.current) return;

        // Cancel any in-progress loadMore request
        if (loadMoreAbortControllerRef.current) {
            loadMoreAbortControllerRef.current.abort();
        }
        loadMoreAbortControllerRef.current = new AbortController();

        setIsLoadingMoreStories(true);
        const currentSkip = stories.length;

        try {
            const response = await api.stories_search(
                lastStoriesQueryRef.current,
                loadMoreAbortControllerRef.current.signal,
                { skip: currentSkip, limit: STORIES_LOAD_MORE_PAGE_SIZE }
            );
            if (response && response.stories) {
                dispatch(appendStoriesAction(response.stories));
                setHasMoreStories(response.hasMore);
                setTotalStories(response.total);
            } else if (response && Array.isArray(response)) {
                // Backwards compatible fallback
                dispatch(appendStoriesAction(response));
                setHasMoreStories(false);
            }
        } catch (err) {
            if (err.name === 'CanceledError' || err.message?.includes('cancel')) {
                return;
            }
            // The next page runs the same query with a bigger skip, so a search
            // that already blew the time budget will never succeed — stop
            // offering a Load more that cannot work.
            if (err.response?.data?.code === 'SEARCH_TIMEOUT') {
                console.warn('Stories search timed out on load-more:', err.response.data.message);
                setHasMoreStories(false);
                return;
            }
            console.error('Error loading more stories:', err);
        } finally {
            setIsLoadingMoreStories(false);
            loadMoreAbortControllerRef.current = null;
        }
    }, [hasMoreStories, isLoadingMoreStories, stories.length]);

    const handleSubmit = async () => {

        if (activeTab === 'searchStories') {
            await handleSubmitStories({ userInitiated: true });
        }
    };

    // Helper function to convert time range string to hours
    const convertTimeRangeToHours = (timeRange) => {
        switch(timeRange) {
            case 'Last Hour':
                return 1;
            case 'Last 2 Hours':
                return 2;
            case 'Last 4 Hours':
                return 4;
            case 'Last 12 Hours':
                return 12;
            case 'Last 24 Hours':
            case 'Live':
                return 24;
            case 'Last 2 Days':
                return 48;
            case 'Last 3 Days':
                return 72;
            case 'Last Week':
                return 168;
            case 'Last Month':
                return 720;
            default:
                return 24;
        }
    };

    const handleSelectedArticleChange = (newSelectedArticles) => {
        //setSelectedArticles(newSelectedArticles);
    };

    const handleSelectedClaimChange = (newSelectedClaims) => {
        setClaimsUpdated(true);
        // setSelectedClaims(newSelectedClaims);
    };

    function arraysAreEqual(array1, array2) {
        if (array1.length !== array2.length) {
            return false; // Arrays have different lengths, so they are not equal
        }

        // Sort both arrays before comparison
        const sortedArray1 = array1.slice().sort();
        const sortedArray2 = array2.slice().sort();

        for (let i = 0; i < sortedArray1.length; i++) {
            if (sortedArray1[i] !== sortedArray2[i]) {
                return false; // Found a difference, so arrays are not equal
            }
        }

        return true; // Arrays are equal, ignoring order
    }

    const handleCreateBrief = async () => {
        await createBriefFromSelectedClaims();
    }
    const handleContinueToBrief = () => {
        // Close the combined modal and create brief with the selections
        setIsPromptModalOpen(false);
        createBriefFromSelectedClaims2(pendingClaimsRef.current);
    };

    const showPromptSelectionModal = (claims) => {
        pendingClaimsRef.current = claims;
        const selectedCount = selectedArticles.filter(a => a.isSelected).length;
        setGenerationMethod(selectedCount <= 3 ? 'claimFree' : 'claims');
        setIsPromptModalOpen(true);
    };

    // Modify to show the combined modal
    const createBriefFromSelectedClaims = async (claims) => {
        // §2 — do not open the dialog while the bucket's settings are still in
        // flight. The method lock is derived from them, so opening early shows
        // an unlocked dropdown that settles a moment later; a request submitted
        // in that window would be inconsistent with the settings about to
        // apply.
        if (targetBucketId && bucketContextLoading) {
            showToastNotification('Loading the target bucket\u2019s settings\u2026', 'info');
            return;
        }
        // Show the combined prompt and language selection modal
        pendingClaimsRef.current = claims;
        const selectedCount = selectedArticles.filter(a => a.isSelected).length;
        // Restricted production is claim-free regardless of how many articles
        // are selected, so the dialog must not open showing 'claims' and then
        // correct itself.
        setGenerationMethod(
            restricted || bucketCompetitorContext?.forceClaimFree || selectedCount <= 3
                ? 'claimFree'
                : 'claims',
        );
        setIsPromptModalOpen(true);
    }

    function getSourceArticles(sources, _articles) {
        const sourceArticles = [];

        // Ensure sources and _articles are arrays
        if (!Array.isArray(sources) || !Array.isArray(_articles)) {
            console.warn('getSourceArticles: sources or _articles is not an array');
            return sourceArticles;
        }

        sources.forEach(name => {
            if (!name) {
                console.warn('getSourceArticles: null or undefined source name');
                return; // Skip this iteration
            }
            
            // Safely find publisher - avoid errors if publishers is undefined or not an array
            const publisher = Array.isArray(publishers) ? 
                publishers.find(pub => pub && pub.name === name) : 
                null;
                
            // Convert name to string to avoid errors when calling toLowerCase
            const nameStr = String(name);  
            const searchKey = publisher && publisher.url ? 
                publisher.url : 
                nameStr.toLowerCase();

            // Find matching article, check url exists before using includes
            const foundArticle = _articles.find(article => 
                article && article.url && typeof article.url === 'string' && 
                article.url.includes(searchKey)
            );

            if (foundArticle) {
                sourceArticles.push({ name, url: foundArticle.url });
            }
        });

        return sourceArticles;
    }

    const createBriefFromSelectedClaims2 = async (claims) => {
        // console.log('createBriefFromSelectedClaims2--',dontShowAgainSetBriefLanguage)

        // Pre-flight. The server enforces this too and returns a 422, but by then
        // a queued job has to fail and the person is told minutes later. The rule
        // is two usable articles from two DISTINCT publishers — two copies of one
        // outlet is one source.
        if (restrictedBlocked) {
            showToastNotification(
                `Not enough matching sources: ${restrictedPublisherCount} publisher`
                + `${restrictedPublisherCount === 1 ? '' : 's'} matches `
                + `${restrictedScopeLabel || 'this suggestion\u2019s filters'}, and two different publishers are required. `
                + 'The suggestion stays accepted.',
                'error',
            );
            return;
        }

        const claimsToHandle = claims ? claims: selectedClaims;
       // console.log('createBriefFromSelectedClaims',userSelectedBriefLanguage,dontShowAgainSetBriefLanguage)

       // console.log('createBriefFromSelectedClaims',claimsToHandle)
        // Clear stale brief data so the previous brief doesn't flash on screen
        setBriefResponse(null);
        setComparisonBriefs(null);
        setLoadingBrief(true);

        // console.log('createBriefFromSelectedClaims2',claims,selectedClaims)

        const claimsWithSources = claimsToHandle.filter(c=>c?.isSelected).map(claim=>
                    {
                        // Safely handle sources, checking each source is not null before mapping
                        const sources = claim.sources && Array.isArray(claim.sources) ? 
                            claim.sources
                                .filter(source => source != null) // Filter out null/undefined sources
                                .map(source => getSourceTitle2(source))
                            : 
                            // Fallback to prSource if available or a default value
                            [selectedArticles && selectedArticles.length > 0 && selectedArticles[0].prSource ? 
                              selectedArticles[0].prSource : 'Unknown Source'];
                              
                        return {
                            claim: claim.title,
                            sources
                        }
                    });
        //
        try {
            const response = await api.startSavedBriefGeneration({
                subject: story?.title || story?.groupSubject?.title || selectedArticles[0]?.subject,
                claims: claimsWithSources,
                language: selectedBriefLanguage,
                storyId: story?._id || selectedArticles[0]?._id,
                articlesTitles: selectedArticles.filter(a => a.isSelected).map(a => a.title).slice(0, 5),
                selectedArticleIds: selectedArticles.filter(a => a.isSelected).map(a => a._id),
                prs,
                promptType: selectedBriefPromptType,
                model: selectedModel,
                reformatModel: selectedReformatModel || undefined,
                translationModel: selectedTranslationModel,
                // Publisher Bucket Intelligence — spec §7: when the target
                // bucket forces Claim-Free (Include mode + competitor sources
                // present), override the state's generationMethod at submit
                // so a stale user selection can't slip through.
                generationMethod: bucketCompetitorContext?.forceClaimFree ? 'claimFree' : generationMethod,
                targetBucketId: targetBucketId || undefined,
                // §1 — production started from a filtered feed applies that
                // filter to its sources. Sent ONLY when there is no target
                // bucket: a bucket-initiated draft is governed by the bucket's
                // setting and its suggestion's saved scope, and the two must not
                // contaminate each other. Unfiltered direct-feed production
                // sends no scope and stays ordinary.
                ...(!targetBucketId && activeCollectionScope
                    ? { initiatedFrom: 'feed', feedScope: activeCollectionScope }
                    : {}),
            });

            if (!response?.id) {
                throw new Error('Missing saved brief id in response');
            }

            dispatch(setLastSelectedClaims(claimsToHandle));
            dispatch(setLastSelectedArticles(selectedArticles));
            setGenerationErrors(null);
            setComparisonBriefs(null);
            setBriefSourceArticles([]);
            window.dispatchEvent(new CustomEvent(SAVED_BRIEF_CREATED_EVENT, {
                detail: { _id: response.id, status: response.status },
            }));
            setIsSavedBriefStartedModalOpen(true);
        } catch (error) {
            // Publisher Bucket Intelligence: 422 with code=competitor_sources_require_claim_free
            // means the target bucket includes competitor sources and only claim-free is allowed.
            const code = error?.response?.data?.code;
            const msg = error?.response?.data?.message;
            if (code === 'competitor_sources_require_claim_free') {
                showToastNotification(
                    msg || 'This story includes competitor sources — switch to Claim-Free brief generation.',
                    'warning',
                );
            } else {
                showToastNotification("We couldn't start generating your brief. Please try again.", 'error');
            }
        } finally {
            setLoadingBrief(false);
        }
    }

    const handleBackToStoriesFromSavedBriefModal = () => {
        try {
            setIsSavedBriefStartedModalOpen(false);
            setNavigateBack(false);
            onSearchClear();
            navigate('/', { replace: true });
        } catch (error) {
            showToastNotification('Something went wrong while redirecting. Please refresh the page.', 'error');
        }
    };

    const onStepClick = async (index) => {
        if (fixtureMode) {
            const tabMap = { 0: 'searchStories', 1: 'searchArticles', 2: 'searchArticles', 3: 'brief' };
            setCurrentStep(index);
            setActiveTab(tabMap[index]);
            return;
        }
        // Readonly users can browse stories (step 0) and view articles (step 1)
        // but cannot advance to Claims (step 2) or Brief Editor (step 3)
        if (!canCreateBrief && index > 1) {
            return;
        }

        setAutoSelect(false);
        setReviewSelectedArticles(false);
        setReviewSelectedClaims(false);

        switch (index) {
            case 0: {
                // §1.1 — when this story was opened from a buckets screen, Back
                // returns THERE rather than to the Stories feed.
                //
                // Navigate straight out without touching currentStep: setting the
                // step first would trip the URL-sync effect, which rewrites the
                // path to '/' and races this navigation. The buckets routes sit
                // outside this component's layout route, so it unmounts cleanly.
                // §7.3 — PEEK, do not consume. The destination screen reads
                // this same payload on mount to restore which row was expanded,
                // its viewport anchor, filters, search and selection, and then
                // clears it itself. Consuming here deleted the entry before
                // Bucket Stories could ever see it, so the row always came back
                // collapsed and unanchored. peekReturn enforces a TTL so an
                // entry that never gets consumed cannot linger indefinitely.
                const returnCtx = peekReturn(null, SLOTS.STORY);
                if (returnCtx?.url) {
                    navigate(returnCtx.url);
                    return;
                }
                if (navigateBack) {
                    // Soft back: preserve stories array, hasMore, totalStories,
                    // scroll position, and lastStoriesQuery so load-more still works.
                    // Only clear story-specific state (articles, claims, selections).
                    // Only skip the refetch if we actually have stories to preserve
                    // (deep-linked entries have an empty stories array)
                    skipNextFetchRef.current = stories.length > 0;
                    setNavigateBack(false);
                    dispatch(clearSelectedArticlesArray());
                    dispatch(clearSelectedClaimsArray());
                    dispatch(updateSelectedClaimsOrder([]));
                    dispatch(setArticles([]));
                    dispatch(setClaims([]));
                    dispatch(setLastSelectedClaims([]));
                    dispatch(setLastSelectedArticles([]));
                    setStoryLocal(null);
                    dispatch(setStory(null));
                    setBriefResponse(null);
                    setComparisonBriefs(null);
                    setBriefSourceArticles([]);
                    clearPersistedBriefState(persistedBriefStorySlug);
                    setReviewSelectedArticles(false);
                    setReviewSelectedClaims(false);
                    setAutoSelect(false);
                    setCurrentStep(0);
                    setActiveTab('searchStories');
                    setLoadingArticles(false);
                } else {
                    setNavigateBack(false);
                    onSearchClear();
                }
                break;
            }

            case 1:
                setNavigateBack(true);
                setActiveTab('searchArticles');
                setCurrentStep(1);
                break;

            case 2:
                // Restricted production never uses claims — shared aggregated
                // claims may carry content from articles outside the scope, so
                // the claims step has nothing to contribute and its output would
                // be discarded.
                //
                // GENERATE, do not navigate. Step 3 is the draft editor and is
                // only reachable by producing a brief; setting the step directly
                // put the route at /draft with no brief, which rendered the
                // source articles again with every control disabled. Claims are
                // passed empty because the claim-free path takes none — the
                // server selects the matching articles itself.
                if (restricted && currentStep === 1) {
                    await createBriefFromSelectedClaims([]);
                    break;
                }
                if (currentStep === 3) {
                    setCurrentStep(2);
                } else if (currentStep === 1) {
                    const areArticlesEqual = arraysAreEqual(
                        lastSelectedArticles.filter(a => a.isSelected).map(a => a._id),
                        selectedArticles.filter(a => a.isSelected).map(a => a._id)
                    );

                    if (areArticlesEqual) {
                        setCurrentStep(2);
                    } else {
                        await handleClaimsBreakdown();
                        setCurrentStep(2);
                    }
                } else {
                    await handleClaimsBreakdown();
                    setCurrentStep(2);
                }
                setActiveTab('searchArticles');
                break;

            case 3:
                if (currentStep === 2) {
                    await createBriefFromSelectedClaims();
                } else if (currentStep === 1) {
                    const claims_ = await handleClaimsBreakdown();
                    const updatedClaims = (claims_ || []).map(claim => ({
                        ...claim,
                        isSelected: true
                    }));
                    await createBriefFromSelectedClaims(updatedClaims);
                } else {
                    await createBriefFromSelectedClaims();
                }
                break;
            case 4:
                handleSmartCopy();
                break;
            default:
                break;
        }
    };

    const handleTryAgain = (step) => {
        setAutoSelect(false);
        setReviewSelectedArticles(false)
        setReviewSelectedClaims(false)

        setCurrentStep(step)
        if (step === 0){
            //dispatch(updateSelectedClaimsOrder([]));
            setNavigateBack(false);
            onSearchClear();
        }
        if (step === 1) {
            dispatch(updateSelectedClaimsOrder([]));
            //setSelectedClaims([]);
            dispatch(setClaims([]))
            setNavigateBack(true);
            setActiveTab('searchArticles')
        }
        if (step === 2) {
            setActiveTab('searchArticles')
        }
    };


    const handleReviewSelectedNewsArticles = () => {
        setReviewSelectedArticles(true);
        //setCurrentStep(2)
    }
    const handleReviewSelectedClaims = () => {
        setReviewSelectedClaims(true);
        //setCurrentStep(2)
    }

    const needToFetchClaims = () => {
        if (claimsUpdated) {
            return true;
        }
        return true;
        //setCurrentStep(2)
    }

    const handleClaimsBreakdown = async () => {
        /*setReviewSelectedArticles(true);
        setCurrentStep(2)*/
        // const story = stories[storyIndex];
        const articleIds = selectedArticles.filter(a=>a.isSelected).map(article => article._id);
        let claimsToReturn;
        setLoadingArticles(true)

        if (prs) {
            setCurrentStep(2)
            await setAutoSelect(true)
            claimsToReturn = selectedArticles.filter(a=>a.isSelected)[0].mainClaims.map(((claim,index)=>
            {return {title:claim,_id:index,type:'claim'}}));
            dispatch(setClaims(claimsToReturn));

        } else {
            try {
                const response = await api.claims(articleIds);
                if (response.clusterGroupClaims.length) {
                    setCurrentStep(2)
                    await setAutoSelect(true)
                    claimsToReturn = response.clusterGroupClaims;
                    
                    // Filter claims based on verification toggle
                    if (verifiedBySourcesToggle && minSourcesVerified) {
                        claimsToReturn = claimsToReturn.filter(claim => 
                            claim.articleCount >= minSourcesVerified
                        );
                    }
                    
                    dispatch(setClaims(claimsToReturn));
                } else {
                    showToastNotification(
                        "Claims aren't ready for this story yet. Please try again in a few minutes.",
                        'info'
                    );
                }
            } catch (err) {
                console.error('Error fetching story claims:', err);
                onStepClick(1)
            }
        }

        dispatch(setLastSelectedArticles(selectedArticles))

        setLoadingArticles(false)
        return claimsToReturn;
    }

    const onSearchClear = () => {
        clearPersistedBriefState(persistedBriefStorySlug);
       // setSelectedArticles([]);
        dispatch(updateSelectedClaimsOrder([]));
        dispatch(clearSelectedArticlesArray());
        dispatch(clearSelectedClaimsArray());
        // Note: Don't clear feedScrollPosition here - it's cleared in ColumnView after scroll restoration
        //setSelectedClaims([]);
        //setTranslatedArts([]);
        setReviewSelectedArticles(false);
        setReviewSelectedClaims(false);
        setCurrentStep(0)
        dispatch(setArticles([]))
        dispatch(setClaims([]))
        dispatch(setLastSelectedClaims([]));
        dispatch(setLastSelectedArticles([]));
        setStoryLocal(null);
        dispatch(setStory(null));
        setClear(true)
        setLoadingArticles(true) // Show spinner immediately while stories fetch
        setActiveTab('searchStories') // This triggers the debounced stories fetch effect
        setFeedResetNonce(n => n + 1) // ...and this guarantees it, even if nothing else changed
    }

    const clearSelectedArticles = () => {
        setAutoSelect(false);
        setReviewSelectedArticles(false)
        selectedArticles.filter(a=>a.isSelected).forEach(article => {
            dispatch(setSelectedArticlesArray({
                article, // the article object
                isSelected:false // the new selection status
            }));

        });
    }

    const clearSelectedClaims = () => {
        setAutoSelect(false);
        setReviewSelectedClaims(false)
        selectedClaims.filter(a=>a.isSelected).forEach(claim => {
            dispatch(setSelectedClaimsArray({
                claim, // the article object
                isSelected:false // the new selection status
            }));

        });
    }
    //console.log('autoSelection1',reviewSelectedArticles,autoSelect,translatedArts.length)
    const autoSelection = (currentStep === 1) ? (!reviewSelectedArticles && autoSelect):
        (!reviewSelectedClaims && autoSelect);

    const isNextStepEnable = () => {
        if (currentStep === 1) {
            return selectedArticles.filter(a=>a.isSelected).length > 0;
        }
        if (currentStep === 2) {
            return selectedClaims.filter(a=>a.isSelected).length > 0;
        }
        if (currentStep === 0) {
            return false;
        }
        if (currentStep === 3) {
            return !!briefResponse;
        }
    }

    // Expose handlers to parent component
    useEffect(() => {
        if (onStepHandlersReady) {
            onStepHandlersReady({
                onStepClick,
                isNextStepEnable,
                switchToFineTuning,
                currentStep,
                canCreateBrief,
                bucketSidebarOpen: isAdmin && bucketSidebarOpen,
                bucketSidebarWidth,
                hasCopiedDraft,
                onSmartCopy: () => {
                    const btn = document.querySelector('.smart-copy-button');
                    if (btn) btn.click();
                },
                onPublishCms: () => {
                    const btn = document.querySelector('.publish-cms-button');
                    if (btn) btn.click();
                },
                isPrimaryActionLoading: loadingBrief && currentStep === 2,
            });
        }
    }, [currentStep, selectedArticles, selectedClaims, onStepHandlersReady, canCreateBrief, bucketSidebarOpen, bucketSidebarWidth, isAdmin, briefResponse, hasCopiedDraft, loadingBrief]);

    const getNumberOfArticles =  () => {
        const arts =  reviewSelectedArticles?selectedArticles.filter(a=>a.isSelected):filteredArticles;
        return arts.length;
    }


    const getNumberOfSelectedArticles =  () => {
        const arts =  selectedArticles.filter(a=>a.isSelected);
        return arts.length;

    }

    const handleMergeStories = async (stories) => {
        // Accept array of stories (2 or more)
        const storyArray = Array.isArray(stories) ? stories : [stories];
        setPendingMergeStories(storyArray);
        setConfirmMergeOpen(true);
    };

    const executeMerge = async () => {
        if (!pendingMergeStories || pendingMergeStories.length < 2) return;
        const storyIds = pendingMergeStories.map(s => s._id);
        setConfirmMergeOpen(false);
        setPendingMergeStories(null);

        try {
            // Start background merge job — returns immediately with a jobId
            const { jobId } = await api.mergeStories({
                groupSubjectIds: storyIds,
                // Backward compat: also send old params for 2-story case
                groupSubject1Id: storyIds[0],
                groupSubject2Id: storyIds[1],
            });

            // Immediately let the user know the merge started — no UI blocking
            showToastNotification('Merging stories… You\'ll be notified when done.', 'info');

            // Poll in background — user can keep working
            pollMergeStatus(jobId);

        } catch (err) {
            console.error('Error starting merge:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Unknown error occurred';
            setErrorModalData({
                title: 'Merge Stories Failed',
                message: errorMessage,
                statusCode: err.response?.status || 500,
                model: null
            });
            setErrorModalOpen(true);
        }
    };

    /**
     * Background polling for merge job completion.
     * Runs without blocking UI — notifies via toast when done.
     */
    const pollMergeStatus = (jobId) => {
        const POLL_INTERVAL_MS = 3000;
        const MAX_POLLS = 60; // 3s × 60 = 3 minutes max
        let polls = 0;

        const intervalId = setInterval(async () => {
            polls++;
            try {
                const status = await api.mergeStoriesStatus(jobId);
                if (status.status === 'completed') {
                    clearInterval(intervalId);
                    showToastNotification('Stories merged successfully! Click to refresh.', 'success', {
                        label: 'Refresh',
                        onClick: () => handleSubmit(),
                    });
                } else if (status.status === 'failed') {
                    clearInterval(intervalId);
                    // If stories were already merged/deleted, suggest refresh instead of showing raw error
                    const isAlreadyMerged = status.error?.includes('no longer exist') || status.error?.includes('not found');
                    if (isAlreadyMerged) {
                        showToastNotification('Stories were already merged. Click to refresh.', 'success', {
                            label: 'Refresh',
                            onClick: () => handleSubmit(),
                        });
                    } else {
                        showToastNotification(status.error || 'Merge failed on server.', 'error');
                    }
                } else if (polls >= MAX_POLLS) {
                    clearInterval(intervalId);
                    showToastNotification('Merge is taking longer than expected. Please refresh in a minute.', 'error');
                }
            } catch (pollErr) {
                console.warn(`merge status poll ${polls} failed:`, pollErr.message);
                if (polls >= MAX_POLLS) {
                    clearInterval(intervalId);
                    showToastNotification('Lost connection to merge job. Please refresh to check status.', 'error');
                }
            }
        }, POLL_INTERVAL_MS);
    };


    const getNumberOfClaims =  () => {
        const arts =  reviewSelectedClaims?selectedClaims.filter(c=>c.isSelected):claims;
        return arts.length;

    }
    const handleExpandStory = async (_story) => {
        if (fixtureMode) {
            setActiveTab('searchArticles');
            setCurrentStep(1);
            return;
        }
        if (!_story?._id && _story?.groupSubject?._id) {
            // Raw feed story (auto-open path) — normalize to the shape StoriesFeed produces
            const rawTitle = _story.title || _story.groupSubject?.title;
            _story = {
                ..._story,
                _id: _story.groupSubject._id,
                slug: _story.slug || _story.groupSubject?.slug,
                title: (rawTitle && typeof rawTitle === 'object') ? rawTitle.text : rawTitle,
            };
        }
        if (!_story?._id) {
            console.error('handleExpandStory called with missing story _id', _story);
            return;
        }
        setLoadingArticles(true);
        try {
            // If story has no slug, fetch via backend to auto-generate one
            let storyWithSlug = _story;
            if (!_story.slug) {
                try {
                    const slugResponse = await api.storyBySlug(_story._id);
                    if (slugResponse?.story?.slug) {
                        storyWithSlug = { ..._story, slug: slugResponse.story.slug };
                    }
                } catch (e) {
                    // Non-critical — continue without slug
                }
            }

            const matchedIds = (_story.articles || []).map(a => a._id || a);
            // The query that produced this row, not the row's article subset.
            // `lastStoriesQueryRef` already holds it verbatim.
            const feedScope = resolveFeedArticleScope(lastStoriesQueryRef.current, _story);
            let response = await api.articlesByStory(_story._id);
            if (response.length) {
                // Skip translation - articles are displayed with original titles
                // Translation happens on-demand in the Article component when excerpt is expanded
                batch(() => {
                    setActiveTab('searchArticles');
                    setCurrentStep(1);
                    setNavigateBack(true); // Enable soft-back to preserve stories + scroll
                    setStoryLocal(storyWithSlug);
                    dispatch(setStory(storyWithSlug));
                    setAutoSelect(true);
                    setOpenedStoryScope(feedScope);
                    dispatch(setArticles(resolveOpenedStoryArticles(response, matchedIds, feedScope)));
                    setLoadingArticles(false);
                });
            } else {
                alert('Processing articles, please wait');
                setLoadingArticles(false);
            }
        } catch (err) {
            console.error('Error fetching story claims:', err);
            onStepClick(1);
            setLoadingArticles(false);
        }
    };

    const closeModal = useCallback(() => {
        setIsLanguageModalOpen(false);
        // setManualLink('');
    }, []);

    const handleNext = useCallback(() => {
        setIsLanguageModalOpen(false);

        // Check if "don't show again" is checked
        if (dontShowAgainSetBriefLanguage) {
            // Save the preference for future
            dispatch(setDontShowAgainSetBriefLanguage(true));
        }

        // Continue with brief creation directly
        createBriefFromSelectedClaims2(pendingClaimsRef.current);
    }, [dontShowAgainSetBriefLanguage]);

    useEffect(() => {
        if (userSelectedBriefLanguage) {
            createBriefFromSelectedClaims2(pendingClaimsRef.current);
            dispatch(setUserSelectedBriefLanguage(false));
        }
    },[userSelectedBriefLanguage])

    // Default GPT translation model for German language (user can change)
    useEffect(() => {
        if (selectedBriefLanguage === 'German') {
            setSelectedTranslationModel('gpt');
        }
    }, [selectedBriefLanguage]);

    // Publisher Bucket Intelligence (spec §7 line 253): when the target bucket
    // is in Include mode, direct-competitor articles must be hidden on the
    // Articles page for that bucket workflow. `hiddenArticleIds` comes from
    // the backend intelligence lookup below.
    const hiddenArticleIds = useMemo(() => {
        return new Set((bucketCompetitorContext?.hiddenArticleIds || []).map(String));
    }, [bucketCompetitorContext]);

    // §4 — one layout, both groups visible. Non-matching articles are no longer
    // filtered out and then revealed by a toggle; they render below the matching
    // ones, muted but readable and openable. Only competitor-hidden articles are
    // actually removed, which is a different rule with a different reason.
    // Restricted production: the server will write the draft only from articles
    // matching the suggestion's saved scope, so the screen must offer exactly
    // that set rather than letting someone select sources the job will discard.
    //
    // DECLARED BEFORE filteredArticles, which reads it. These are `const`, so a
    // useMemo above this point that referenced them threw "Cannot access
    // 'restricted' before initialization" during render — which blanks the whole
    // app, because the error escapes before anything is painted. A production
    // build compiles this happily; the temporal dead zone is a runtime rule.
    // Two ways in, deliberately distinct (§1):
    //  - a bucket-initiated draft, where the server resolves the suggestion's
    //    saved scope for this (bucket, story) pair; and
    //  - a feed-initiated draft, where the restriction is the feed's own active
    //    filter and there is no bucket to ask.
    // Never both: a feed filter must not leak into bucket production, and a
    // bucket setting must not govern a draft started from the feed.
    const feedRestricted = !targetBucketId && Boolean(activeCollectionScope);
    const restricted = bucketCompetitorContext?.restrictedProduction === true || feedRestricted;

    const feedScopeLabel = useMemo(() => {
        if (!activeCollectionScope) return null;
        const parts = [];
        if (activeCollectionScope.languages?.length) parts.push(`${activeCollectionScope.languages.join(', ')}-language`);
        if (activeCollectionScope.countries?.length) parts.push(activeCollectionScope.countries.join(', '));
        return parts.length ? `${parts.join(' ')} articles only` : null;
    }, [activeCollectionScope]);

    const restrictedScopeLabel = feedRestricted
        ? feedScopeLabel
        : (bucketCompetitorContext?.restrictedScopeLabel || null);

    // Positively in the feed's scope: inside its time window AND not excluded by
    // its filter. Written as an allow-list rather than `!a.extra`, because an
    // article can now be out of scope for the window alone — and a draft the
    // server will restrict must offer exactly the set the server will accept.
    const inFeedScope = useCallback(
        (a) => Boolean(a) && !a.extra && !a.outsideTimeRange, []);

    const restrictedMatchingIds = useMemo(() => {
        if (feedRestricted) {
            // The feed already marked which articles matched; these flags are
            // that decision and the same rule the server will apply.
            return new Set(articles.filter(inFeedScope).map(a => String(a._id)));
        }
        return new Set((bucketCompetitorContext?.matchingArticleIds || []).map(String));
    }, [feedRestricted, articles, bucketCompetitorContext, inFeedScope]);

    const restrictedPublisherCount = useMemo(() => {
        if (!feedRestricted) return bucketCompetitorContext?.matchingPublisherCount || 0;
        return new Set(
            articles.filter(inFeedScope)
                .map(a => String(a?.source?.uri || a?.source?.title || '').toLowerCase())
                .filter(Boolean),
        ).size;
    }, [feedRestricted, articles, bucketCompetitorContext, inFeedScope]);
    // Checked BEFORE submission. The server enforces this too, but failing an
    // asynchronous job is a poor way to tell someone their suggestion has one
    // usable publisher.
    const restrictedBlocked = restricted && restrictedPublisherCount < 2;

    const filteredArticles = useMemo(() => {
        const visible = hiddenArticleIds.size === 0
            ? articles
            : articles.filter(art => !hiddenArticleIds.has(String(art?._id)));
        if (!restricted) return visible;
        // Under a restriction the authoritative set comes from the SERVER —
        // the suggestion's saved scope — not from whatever filter the feed
        // happens to have active now. `lockedOutOfScope` is deliberately
        // separate from `hidden`: these articles stay readable and openable,
        // they just cannot be a source for this draft.
        return visible.map(art => ({
            ...art,
            lockedOutOfScope: !restrictedMatchingIds.has(String(art?._id)),
        }));
    }, [articles, hiddenArticleIds, restricted, restrictedMatchingIds]);

    // Fetch the (bucket, story) intelligence context whenever both are set so
    // the article filter and the generation-method lock can react before the
    // user hits Generate. Debounced/aborted so a rapid bucket switch doesn't
    // race a stale response.
    useEffect(() => {
        const storyId = story?._id || story?.groupSubject?._id;
        if (!targetBucketId || !storyId) {
            setBucketCompetitorContext(null);
            return undefined;
        }
        const controller = new AbortController();
        let cancelled = false;
        (async () => {
            try {
                setBucketContextLoading(true);
                const info = await api.getBucketStoryIntelligence(targetBucketId, storyId, {
                    signal: controller.signal,
                });
                if (cancelled) return;
                // Restricted production is claim-free by definition — shared
                // aggregated claims may incorporate articles from outside the
                // scope. Folded into the SAME flag the competitor lock uses, so
                // every place that already honours forceClaimFree honours this
                // too rather than growing a second rule.
                const forceClaimFree = (info?.hasCompetitorSources && !info?.excludeDirectCompetitorStories)
                    || !!info?.restrictedProduction;
                setBucketCompetitorContext({
                    ...info,
                    forceClaimFree: !!forceClaimFree,
                });
            } catch (_err) {
                if (!cancelled) setBucketCompetitorContext(null);
            } finally {
                if (!cancelled) setBucketContextLoading(false);
            }
        })();
        return () => { cancelled = true; controller.abort(); };
    }, [targetBucketId, story?._id, story?.groupSubject?._id]);

    // Four populations, not two. "Did not match the filter", "was published
    // outside the time range" and "has not finished processing" are different
    // facts with different causes, and only the first is evidence about the
    // filter — conflating them is what made a 100% match read as 2 of 3, and
    // what labelled an older article "Outside this filter" with no filter set.
    // An unready or out-of-window article is never counted as a non-match.
    const awaitingArticleCount = useMemo(
        () => filteredArticles.filter(a => a && isAwaitingProcessing(a)).length, [filteredArticles]);
    const matchingArticleCount = useMemo(
        () => filteredArticles.filter(a => a && !a.extra && !a.outsideTimeRange && !isAwaitingProcessing(a)).length,
        [filteredArticles]);
    const nonMatchingArticleCount = useMemo(
        () => filteredArticles.filter(a => a && a.extra && !isAwaitingProcessing(a)).length, [filteredArticles]);
    const outsideTimeRangeCount = useMemo(
        () => filteredArticles.filter(a => a && a.outsideTimeRange && !isAwaitingProcessing(a)).length,
        [filteredArticles]);

    const storyScopeSummary = useMemo(() => {
        const parts = [];
        // "matching this filter" is only meaningful against something it
        // excludes. With no filter active every article matches, and saying so
        // next to a processing count invited exactly the wrong subtraction.
        if (nonMatchingArticleCount > 0) {
            parts.push(`${matchingArticleCount} matching this filter`);
            parts.push(`${nonMatchingArticleCount} other`);
        }
        // Named, because this is the population that used to be reported as a
        // filter miss. Only the "Last N" labels are quoted back: 'Live' is also
        // a 24-hour window, so "outside Live" would describe the wrong thing.
        if (outsideTimeRangeCount > 0) {
            const range = openedStoryScope?.timeRangeLabel;
            parts.push(/^Last /.test(range || '')
                ? `${outsideTimeRangeCount} outside ${range}`
                : `${outsideTimeRangeCount} outside the selected time range`);
        }
        if (awaitingArticleCount > 0) parts.push(`${awaitingArticleCount} still processing`);
        return parts.join(', ');
    }, [matchingArticleCount, nonMatchingArticleCount, outsideTimeRangeCount, awaitingArticleCount,
        openedStoryScope]);

    const hasExtraStoryArticles = useMemo(() => {
        return articles.some((item) => item?.extra || item?.outsideTimeRange || isAwaitingProcessing(item));
    }, [articles]);

    // Function to show toast
    const showToastNotification = (message, type = 'success', action = null) => {
        setToastMessage(message);
        setToastType(type);
        setToastAction(action);
        setShowToast(true);
        
        // Hide toast after 3s (or 8s if it has an action button)
        setTimeout(() => {
            setShowToast(false);
            setToastAction(null);
        }, action ? 8000 : 3000);
    };

    // Updated handleSmartCopy function
    const handleSmartCopy = () => {
        let textToCopy = '';
        
        // Determine what to copy based on the current step
        switch (currentStep) {
            case 0: // Stories
                if (story) {
                    textToCopy = story.title || story.groupSubject?.title || 'No story title';
                }
                break;
            case 1: // Articles
                if (selectedArticles.filter(a => a.isSelected).length > 0) {
                    textToCopy = selectedArticles
                        .filter(a => a.isSelected)
                        .map(a => `${a.title}\n${a.url}`)
                        .join('\n\n');
                }
                break;
            case 2: // Claims
                if (selectedClaims.filter(c => c.isSelected).length > 0) {
                    textToCopy = selectedClaims
                        .filter(c => c.isSelected)
                        .map(c => c.content)
                        .join('\n\n');
                }
                break;
            case 3: // Brief Editor
                if (briefResponse) {
                    textToCopy = briefResponse;
                }
                break;
            default:
                textToCopy = '';
        }

        // Copy the text to clipboard
        if (textToCopy) {
            navigator.clipboard.writeText(textToCopy)
                .then(() => {
                    showToastNotification('Content copied to clipboard!');
                    if (currentStep === 3) markOnboardingStep('copiedDraft');
                })
                .catch(err => {
                    console.error('Failed to copy content: ', err);
                    showToastNotification('Failed to copy content to clipboard.', 'error');
                });
        } else {
            showToastNotification('No content available to copy.', 'error');
        }
    };

    return (
        <div
            className={`main-flex-container ${currentStep === 0 ? 'with-category-bar' : ''}`}
            style={(currentStep === 0 || currentStep === 1) && isAdmin && bucketSidebarOpen ? { marginRight: bucketSidebarWidth, transition: 'margin-right 0.25s ease' } : { transition: 'margin-right 0.25s ease' }}
        >
            {activeTab === 'fineTuning' && (
                    <FineTuning
                        setActiveTab={setActiveTab}
                    />
            )}
            <div className="side-by-side-container">
                {/* ===== Step 3: Brief Editor (single centered column) ===== */}
                {activeTab === 'brief' && (
                    <div className="screen-brief">
                        <div className="screen-brief-inner">
                            <BriefComponent
                                briefResponse={fixtureMode ? FIXTURE_BRIEF : briefResponse}
                                setSelectedTitleIndex={setSelectedTitleIndex}
                                selectedTitleIndex={selectedTitleIndex || 0}
                                setBriefResponse={fixtureMode ? (() => {}) : setBriefResponse}
                                handleTryAgain={handleTryAgain}
                                briefSourceArticles={fixtureMode ? FIXTURE_ARTICLES : briefSourceArticles}
                                selectedClaims={fixtureMode ? FIXTURE_CLAIMS : selectedClaims.filter(c=>c.isSelected)}
                                storyTitle={fixtureMode ? FIXTURE_STORY.title : (story?.title || story?.groupSubject?.title || selectedArticles[0]?.subject)}
                                storyId={fixtureMode ? FIXTURE_STORY._id : (story?._id || story?.groupSubject?._id)}
                                language={fixtureMode ? 'English' : selectedBriefLanguage}
                                isRtl={fixtureMode ? false : (selectedBriefLanguage === 'Hebrew' || selectedBriefLanguage ==='Arabic')}
                                selectedCategory={selectedCategories}
                                comparisonBriefs={fixtureMode ? null : comparisonBriefs}
                                generationErrors={fixtureMode ? null : generationErrors}
                                onToast={showToastNotification}
                                onCopySuccess={() => { setHasCopiedDraft(true); markOnboardingStep('copiedDraft'); }}
                            />
                        </div>
                    </div>
                )}

                {/* ===== Step 0: Stories Feed (3-column) ===== */}
                {/* Kept mounted (display:none) to preserve scroll position */}
                <div className="sf-layout" style={activeTab !== 'searchStories' ? { display: 'none' } : undefined}>
                    <div className={`sf-sidebar${sidebarCollapsed ? ' sf-sidebar-collapsed' : ''}`}>
                        <div className="sf-sidebar-header">
                            {!sidebarCollapsed && (
                                <span className="sf-sidebar-label">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0B1E3F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                                    </svg>
                                    Filters
                                </span>
                            )}
                            <button
                                className="sf-sidebar-toggle"
                                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                                title={sidebarCollapsed ? 'Show filters' : 'Hide filters'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                    style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease' }}
                                >
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                            </button>
                        </div>
                        {!sidebarCollapsed && (
                            <SearchFields
                                currentStep={currentStep}
                                onSearchSubmit={handleSubmit}
                                withSearchFields={true}
                                onSearchClear={onSearchClear}
                                categories={categories}
                                languages={languages}
                                excludeTheme={() => { }}
                            />
                        )}
                    </div>
                    <div className="sf-center">
                        <StoriesFeed
                            collectionScope={activeCollectionScope}
                            stories={fixtureMode ? FIXTURE_STORIES : stories}
                            errorStrories={fixtureMode ? null : errorStrories}
                            expandedTimeRange={expandedTimeRange}
                            handleExpandStory={handleExpandStory}
                            handleMergeStories={canEdit ? handleMergeStories : undefined}
                            sortBy={selectedTimeRange === 'Live' ? 'recent' : 'size'}
                            isLoading={fixtureMode ? false : loadingArticles}
                            onLoadMore={fixtureMode ? (() => {}) : handleLoadMoreStories}
                            hasMoreStories={fixtureMode ? false : hasMoreStories}
                            isLoadingMore={fixtureMode ? false : isLoadingMoreStories}
                            mergeStories={mergeStories}
                            showToastNotification={showToastNotification}
                            onIdleRefresh={fixtureMode ? undefined : handleIdleRefresh}
                            currentStep={currentStep}
                            /* Spec §16 — the Irrelevant signal is scoped to ONE category, so
                               it is only offered when exactly one is filtered. With "All
                               categories" or a multi-select there is no unambiguous
                               category to record the signal against. */
                            activeCategory={
                                Array.isArray(selectedCategories) && selectedCategories.length === 1
                                    ? selectedCategories[0]
                                    : null
                            }
                            onCategoryOptimizeTrigger={(trigger) => {
                                // §16.4 — non-blocking, opens in a NEW TAB, preselects the
                                // category, and never asks for written feedback first.
                                if (trigger?.categorySlug) {
                                    window.open(`/admin2/categories?improve=${encodeURIComponent(trigger.categorySlug)}`, '_blank', 'noopener');
                                }
                            }}
                        />
                    </div>
                </div>

                {/* ===== Steps 1 & 2: Articles / Claims (2-zone layout) ===== */}
                {activeTab === 'searchArticles' && (
                    <div className="screen-2zone">
                        <div className="screen-2zone-summary">
                            {currentStep === 1 && (
                                <>
                                    <div className="screen-2zone-summary-count">
                                        Showing {fixtureMode ? FIXTURE_ARTICLES.length : getNumberOfArticles()} articles for the selected story
                                    </div>
                                    <div
                                        ref={isAdmin && storyId ? storyDragRef : undefined}
                                        className={`screen-2zone-summary-title${isAdmin && storyId ? ' admin-draggable' : ''}`}
                                    >
                                        {fixtureMode ? FIXTURE_STORY.title : (story ? story?.title || story?.groupSubject?.title : storyLocal?.title)}
                                    </div>
                                    {isAdmin && storyId && (
                                        <button
                                            className="screen-2zone-copy-all"
                                            // `display: flex` rather than the class's inline-flex:
                                            // the scope line that follows otherwise runs onto the
                                            // same line, which the step 2 copy button never showed
                                            // because nothing follows it.
                                            style={{ marginBottom: 16, display: 'flex' }}
                                            onClick={() => setAddToBucketOpen(true)}
                                        >
                                            <InventoryOutlinedIcon style={{ fontSize: 14 }} />
                                            Add to bucket
                                        </button>
                                    )}
                                    {/* The "Include all story articles" toggle is gone (§4): nothing
                                        is hidden any more, so there is nothing to reveal. Matching
                                        articles render first, non-matching below in a muted group. */}
                                    {hasExtraStoryArticles && (
                                        <span className="screen-2zone-summary-scope">
                                            {storyScopeSummary}
                                        </span>
                                    )}
                                    {restricted && (
                                        <span
                                            className="screen-2zone-summary-scope"
                                            style={{
                                                display: 'block',
                                                marginTop: '4px',
                                                color: restrictedBlocked ? '#c62828' : '#3C4A5E',
                                            }}
                                        >
                                            {restrictedBlocked
                                                ? `Draft sources: ${restrictedScopeLabel || 'the suggestion\u2019s filters'} — only ${restrictedPublisherCount} matching publisher, two are required, so this draft cannot be generated.`
                                                : `Draft sources: ${restrictedScopeLabel || 'the suggestion\u2019s filters'} (${restrictedPublisherCount} matching publishers).`}
                                        </span>
                                    )}
                                </>
                            )}
                            {currentStep === 2 && (
                                <>
                                    <div className="screen-2zone-summary-title">
                                        {fixtureMode ? FIXTURE_STORY.title : (story ? story?.title || story?.groupSubject?.title : storyLocal?.title)}
                                    </div>
                                    <div className="screen-2zone-summary-stats">
                                        Showing <strong>{fixtureMode ? FIXTURE_CLAIMS.length : getNumberOfClaims()}</strong> claims extracted from <strong>{fixtureMode ? '21' : getNumberOfArticles()}</strong> sources.
                                    </div>
                                    <button
                                        className="screen-2zone-copy-all"
                                        onClick={() => {
                                            const allClaims = claims.map(c => c.text || c.claim || c.title).join('\n\n');
                                            navigator.clipboard.writeText(allClaims).then(() => {
                                                showToastNotification('All claims copied!', 'success');
                                            });
                                        }}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                                        </svg>
                                        Copy all claims
                                    </button>
                                </>
                            )}
                        </div>
                        <div className="screen-2zone-main">
                            {currentStep === 1 && (
                                <div className="sa-header">
                                    <span className="sa-header-title">Select sources to generate an article draft</span>
                                    <div className="sa-header-actions">
                                        <button className="sa-select-btn" onClick={() => {
                                            const arts = reviewSelectedArticles ? selectedArticles.filter(a=>a.isSelected) : filteredArticles;
                                            arts.forEach(a => {
                                                dispatch(setSelectedArticlesArray({ article: a, isSelected: true }));
                                            });
                                            setAutoSelect(true);
                                        }}>
                                            ✓ Select All
                                        </button>
                                        <button className="sa-select-btn" onClick={clearSelectedArticles}>
                                            ✗ Unselect All
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className={`sa-list-container${currentStep === 2 ? ' sa-list-claims' : ''}`}>
                                {!fixtureMode && loadingArticles ? (
                                    <div className="screen-loading">
                                        <LottieLoader size={300} />
                                    </div>
                                ) : (
                                    <ArticlesList
                                        storyTitle={fixtureMode ? FIXTURE_STORY.title : (story ? story?.title || story?.groupSubject?.title : storyLocal?.title)}
                                        articles={fixtureMode ? FIXTURE_ARTICLES : (reviewSelectedArticles ? selectedArticles.filter(a=>a.isSelected) : filteredArticles)}
                                        claims={fixtureMode ? FIXTURE_CLAIMS : (reviewSelectedClaims ? selectedClaims.filter(c=>c.isSelected) : claims)}
                                        onSelectedArticleChange={handleSelectedArticleChange}
                                        onSelectedClaimChange={handleSelectedClaimChange}
                                        publicationsData={publicationsData.length ? publicationsData : null}
                                        publishers={publishers}
                                        autoSelection={autoSelection}
                                        theme={selectedPublishers}
                                        currentStep={currentStep}
                                        prs={prs}
                                        showToastNotification={showToastNotification}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* Prompt and Language Selection Modal (Combined) */}
            <div ref={promptModalRef}>
                <BriefGenerationSettingsModal
                    isOpen={isPromptModalOpen}
                    title={story?.title || story?.groupSubject?.title || selectedArticles[0]?.subject || 'Brief'}
                    canEdit={canEdit}
                    canSelectGenerationMethod={_canCreateBrief}
                    // Deliverable languages, not the source-filter list: a brief
                    // is translated into this, and only 33 of the 89 languages we
                    // can read are ones we can write.
                    languages={OUTPUT_LANGUAGE_NAMES}
                    values={{
                        promptType: selectedBriefPromptType,
                        language: selectedBriefLanguage,
                        model: selectedModel,
                        reformatModel: selectedReformatModel,
                        translationModel: selectedTranslationModel,
                        // Publisher Bucket Intelligence — spec §7. When the target
                        // bucket forces Claim-Free, the modal displays the
                        // locked value regardless of the state.
                        generationMethod: bucketCompetitorContext?.forceClaimFree ? 'claimFree' : generationMethod,
                        targetBucketId,
                    }}
                    onChange={(key, value) => {
                        if (key === 'promptType') setSelectedBriefPromptType(value);
                        if (key === 'language') dispatch(setSelectedBriefLanguage(value));
                        if (key === 'model') setSelectedModel(value);
                        if (key === 'reformatModel') setSelectedReformatModel(value);
                        if (key === 'translationModel') setSelectedTranslationModel(value);
                        if (key === 'generationMethod') setGenerationMethod(value);
                        if (key === 'targetBucketId') {
                            setTargetBucketId(value);
                            // A bucket's targetLanguage is the language that bucket
                            // wants its drafts produced in, so picking one sets the
                            // Language select — visibly, and still overridable after.
                            // "None" leaves whatever is selected alone.
                            const bucketLanguage = resolveBucketLanguage([adminBuckets, ownedBuckets], value);
                            if (bucketLanguage) dispatch(setSelectedBriefLanguage(bucketLanguage));
                            // Remember it — including a deliberate "None" — so the
                            // next draft opens on the same setting.
                            if (!isAdmin && reformatChoiceKey) {
                                try { window.localStorage.setItem(reformatChoiceKey, value || ''); } catch (_e) { /* private mode */ }
                            }
                        }
                    }}
                    isAdmin={isAdmin}
                    buckets={adminBuckets}
                    ownedBuckets={ownedBuckets}
                    competitorContext={bucketCompetitorContext}
                    onCancel={() => setIsPromptModalOpen(false)}
                    onContinue={() => handleContinueToBrief()}
                    showCreateNewCopyOption={false}
                    isSubmitting={loadingBrief}
                    continueLabel="Generate draft"
                    modalTestId="brief-generation-settings-modal"
                />
            </div>

            <AppModal
                isOpen={isSavedBriefStartedModalOpen}
                title="Your draft is being generated"
                footer={(
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '20px' }}>
                        {!onboardingFirstDraftPending && (
                            <button
                                type="button"
                                onClick={handleBackToStoriesFromSavedBriefModal}
                                style={{
                                    padding: '10px 24px',
                                    backgroundColor: '#fff',
                                    color: '#FF6B35',
                                    border: '1px solid #FF6B35',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                }}
                            >
                                Back to stories
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => {
                                setIsSavedBriefStartedModalOpen(false);
                                navigate('/my-drafts');
                            }}
                            style={{
                                padding: '10px 24px',
                                backgroundColor: '#FF6B35',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 500,
                            }}
                        >
                            {onboardingFirstDraftPending ? 'Go to my draft' : 'My Drafts'}
                        </button>
                    </div>
                )}
            >
                <p>
                    {onboardingFirstDraftPending
                        ? 'We’ll notify you when it’s ready. Let’s visit your drafts page — that’s where every draft you create lives.'
                        : 'We’ll notify you when it’s ready. You can also find it in My Drafts while it’s in progress.'}
                </p>
            </AppModal>
            
            {/* Toast Notification */}
            {showToast && (
                <div className={`toast-notification ${toastType}`} style={{
                    position: 'fixed',
                    top: '72px',
                    right: '20px',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    backgroundColor: toastType === 'info' ? '#00AEEF' : '#FF6B35',
                    color: 'white',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                    zIndex: 9999,
                    opacity: 1,
                    transition: 'opacity 0.3s ease-in-out',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                }}>
                    <span>{toastMessage}</span>
                    {toastAction && (
                        <button
                            onClick={() => { toastAction.onClick(); setShowToast(false); setToastAction(null); }}
                            style={{
                                background: 'rgba(255,255,255,0.25)',
                                border: '1px solid rgba(255,255,255,0.5)',
                                borderRadius: '4px',
                                color: '#fff',
                                padding: '4px 12px',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '13px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {toastAction.label}
                        </button>
                    )}
                </div>
            )}

            {/* Merge Confirmation Dialog */}
            <AppModal
                isOpen={Boolean(confirmMergeOpen && pendingMergeStories)}
                title="Merge Stories?"
                onCancel={() => { setConfirmMergeOpen(false); setPendingMergeStories(null); }}
                onConfirm={executeMerge}
                confirmLabel="Merge"
                maxWidth={460}
            >
                <p>
                    Are you sure you want to merge these {pendingMergeStories?.length} stories into one? This action cannot be undone.
                </p>
                <div style={{ fontSize: '13px', color: '#777', marginTop: '8px', lineHeight: 1.5, maxHeight: '200px', overflowY: 'auto' }}>
                    {pendingMergeStories?.map((story, idx) => (
                        <div key={story._id} style={{ marginBottom: '4px' }}>
                            <strong>{idx + 1}.</strong> {story.title?.substring(0, 80)}{story.title?.length > 80 ? '...' : ''}
                            {story.articles?.length ? ` (${story.articles.length} articles)` : ''}
                        </div>
                    ))}
                </div>
            </AppModal>
            
            {/* Error Modal for brief generation failures */}
            <ErrorModal
                isOpen={errorModalOpen}
                onClose={() => setErrorModalOpen(false)}
                title={errorModalData.title}
                message={errorModalData.message}
                statusCode={errorModalData.statusCode}
                model={errorModalData.model}
                onRetry={() => {
                    setErrorModalOpen(false);
                    // User can change model and retry
                }}
                retryLabel="Close & Try Different Model"
            />
            {/* Publisher Buckets sidebar (visible at steps 0-1 for admins only) */}
            {(currentStep === 0 || currentStep === 1) && isAdmin && (
                <StoriesBucketPanel
                    isOpen={bucketSidebarOpen}
                    onToggle={toggleBucketSidebar}
                    onWidthChange={setBucketSidebarWidth}
                    // Drag-and-drop onto a bucket is the same manual add as the
                    // row action and must record the same scope. Omitting this
                    // prop silently produced suggestions with collectionScope
                    // null, so a scoped drag looked identical to an unscoped one
                    // and restricted production correctly declined to fire.
                    collectionScope={activeCollectionScope}
                />
            )}
            {/* Mounted only while open, because the picker loads every bucket
                on mount and this screen renders for every story opened. */}
            {addToBucketOpen && openedStoryForBucket && (
                <AddToBucketModal
                    open
                    story={openedStoryForBucket}
                    collectionScope={activeCollectionScope}
                    onClose={() => setAddToBucketOpen(false)}
                    showToastNotification={showToastNotification}
                />
            )}
        </div>
    )
};

export default CreateBriefComponent;
